import axios from "axios";
import useSWR, { useSWRImmut } from "./useSWR";
import { useCallback, useState } from "react";
import { cloneDeep } from "lodash";
import { resultsFunction as guidedSelling } from "../utils/resultFunctions/guidedSelling";
import { resultsFunction as guidedSellingAU } from "../utils/resultFunctions/guidedSellingAU";
import { resultsFunction as defaultResultsFunction } from "../utils/resultFunctions/defaultResultsFunction";
import {
  isEligibleWw,
  getResultsPageEli,
  getResultsJoinSequence,
  WAITLIST_REASON_IRRETRIEVABLE
} from "./eligibility";
import {
  getIsClinicEligibileUni,
  getResultsPageUni
} from "./resultFunctions/unifiedAssessment";
import { getConfigByLocale } from "../config/global";
import getSessionData from "./getSessionData";
import {
  getIsClinicEligibleUni4,
  getResultsPageUniV4
} from "./resultFunctions/unifiedV4";
import {
  getIsClinicEligibleUni3,
  getResultsPageUniV3
} from "./resultFunctions/unifiedV3";
import { updateAssessmentId } from "./account";
import { logger } from "./logger";

const assessmentService = axios.create({
  baseURL: window.wwBasePath.getAttribute("href"),
  timeout: 5000
});

const assessmentsService = axios.create({
  baseURL: window.wwBasePath.getAttribute("href"),
  timeout: 3000
});

const assessmentRecipeService = axios.create({
  baseURL: window.wwBasePath.getAttribute("href"),
  timeout: 5000
});

const assessmentCoachService = axios.create({
  baseURL: window.wwBasePath.getAttribute("href"),
  timeout: 5000
});

const assessmentProfileService = axios.create({
  baseURL: window.wwBasePath.getAttribute("href"),
  timeout: 5000
});

const sequenceService = axios.create({
  baseURL: window.ww.sequenceBaseUrl,
  timeout: 5000
});

const smsService = axios.create({
  baseURL: window.ww.wwAPI,
  timeout: 5000
});

function fetchAssessments([, slugs]) {
  const url = `/api/assessments?include=${slugs}`;
  return assessmentsService.get(url);
}

export function fetchRecipes([, cuisines, dietaryPreferences]) {
  const url = `/api/recipe-filter`;
  return assessmentRecipeService.get(url, {
    params: { cuisines, dietaryPreferences }
  });
}

export function useFetchRecipes(cuisines, dietaryPreferences) {
  return useSWRImmut(["recipes", cuisines, dietaryPreferences], fetchRecipes);
}

export async function fetchCoaches(coachingStyles) {
  const url = `/api/coach-filter`;
  return await assessmentCoachService.get(url, {
    params: { coachingStyles, market: window.ww.locale }
  });
}

export async function fetchProfile() {
  const url = "/api/profile";
  return assessmentProfileService.get(url);
}

export async function fetchClinicalEligibility([, locale]) {
  const { uuid, token } = getSessionData();

  logger("Fetching clinical eligibility", {
    level: "info"
  });

  const clinicalEligibility = await smsService.post(
    `/sms/v3/subscriptions/clinical/eligibility?locale=${locale}${uuid ? `&uuid=${uuid}` : ""}`,
    null,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  logger("Succesfully fetched clinical eligibility", {
    level: "info"
  });

  return clinicalEligibility;
}

export function useFetchClinicalEligibility(locale) {
  return useSWR(["clinicalEligibility", locale], fetchClinicalEligibility, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false
  });
}

export function useAssessments(slugs) {
  slugs = slugs.join();
  return useSWR(["assessments", slugs], fetchAssessments);
}

function sanitizeAnswers(answers, questions) {
  const sanitizedAnswers = cloneDeep(answers);

  sanitizedAnswers.forEach(answersPerPage => {
    answersPerPage.answers.forEach(answer => {
      if (answer.id) {
        delete answer.id;
      }
    });

    const question = questions.find(q => q._id === answersPerPage.questionId);
    answersPerPage.questionHeaderText1 = question?.headerText1Analytics
      ? question.headerText1Analytics
      : "";
  });

  return sanitizedAnswers;
}

const removeSensitiveData = answersArray => {
  const clonedAnswersArray = cloneDeep(answersArray);
  clonedAnswersArray.forEach(answer => {
    if (answer?.profile?.firstName) {
      delete answer.profile.firstName;
    }
    if (answer?.profile?.lastName) {
      delete answer.profile.lastName;
    }
    if (answer?.profile?.uuid) {
      delete answer.profile.uuid;
    }
    if (answer?.profile?.medical?.email) {
      delete answer.profile.medical.email;
    }
  });

  return clonedAnswersArray;
};

export async function submitDataToDb(answers, questions, slugs) {
  try {
    const cleanedAnswers = removeSensitiveData(answers);

    const response = await assessmentService.post(
      "/api/assessments/new-quiz/save/v2",
      {
        answers: sanitizeAnswers(cleanedAnswers, questions),
        slugs,
        result: null,
        finished: false
      }
    );

    return response?.data?.id;
  } catch (error) {
    logger("Error saving data to DB", {
      level: "error",
      customAttributes: { error }
    });
  }
}

function getSuggestedProduct(newQuiz, answers, questions) {
  if (
    newQuiz.eli ||
    newQuiz.isSequence ||
    newQuiz.isUni ||
    newQuiz.isUni3 ||
    newQuiz.isUni4
  ) {
    return defaultResultsFunction();
  }

  const resultsFuncName = getConfigByLocale(window.ww.locale)[
    "guidedSellingResultsFunction"
  ];

  const funcNameToFuncMapping = {
    guidedSellingAU,
    guidedSelling
  };

  return funcNameToFuncMapping[resultsFuncName](answers, questions);
}

export function saveUuid({ assessmentId, uuid }) {
  return assessmentService.post("/api/assessments/update-uuid", {
    assessmentId,
    uuid
  });
}

export async function fetchSequenceEligibility(
  assessmentId,
  shouldAllow27to30BmiNoComorbidity = "false"
) {
  try {
    if (!assessmentId) {
      const msg = "Error: no aid found when getting waitlist reason";
      logger(msg, {
        level: "error"
      });
      throw new Error(msg);
    }
    const { token } = getSessionData();

    logger("Getting waitlist reason", {
      level: "info"
    });

    const eligibilityResponse = await sequenceService.get(
      `/api/v1/ww/waitlist-reason?wwAssessmentId=${assessmentId}&shouldAllow27to30BmiNoComorbidity=${shouldAllow27to30BmiNoComorbidity}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    logger("Successfully got waitlist reason", {
      level: "info",
      customAttributes: { assessmentId }
    });

    return eligibilityResponse?.data?.waitlistReason;
  } catch (error) {
    logger("Error getting waitlist reason", {
      level: "info",
      customAttributes: { error }
    });
    return WAITLIST_REASON_IRRETRIEVABLE;
  }
}

export function useSubmitAssessment() {
  const MODE_PRISTINE = "PENDING";
  const MODE_SUBMITTING = "SUBMITTING";
  const MODE_SUCCESS = "SUCCESS";
  const MODE_FAILURE = "FAILURE";
  const [assessmentId, setAssessmentId] = useState(null);
  const [suggestedProduct, setSuggestedProduct] = useState();
  const [mode, setMode] = useState(MODE_PRISTINE);
  const [eligibleSequence, setEligibleSequence] = useState(false);
  const [resultsScreen, setResultsScreen] = useState("");

  const submit = useCallback(
    async ({
      answers,
      questions,
      newQuiz,
      userPredictedToReachGoal,
      userRecommendedPlanLength,
      createIneligibleUser,
      profile,
      market
    }) => {
      setMode(MODE_SUBMITTING);
      try {
        const product = getSuggestedProduct(newQuiz, answers, questions);

        let sequenceEligible = false;
        let result = "";

        if (newQuiz.isUni3) {
          sequenceEligible = getIsClinicEligibleUni3(answers, questions);
          result = getResultsPageUniV3(answers, questions);
        } else if (newQuiz.isUni4) {
          sequenceEligible = getIsClinicEligibleUni4(
            answers,
            questions,
            profile
          );
          result = getResultsPageUniV4(answers, questions, profile);
        } else if (newQuiz.isUni) {
          sequenceEligible = getIsClinicEligibileUni(answers, questions);
          result = getResultsPageUni(answers, questions);
        } else if (newQuiz.eli || newQuiz.isSequence) {
          const id = await submitDataToDb(answers, questions, newQuiz.dbSlugs);
          const shouldAllow27to30BmiNoComorbidity = !profile?.isB2B;
          const waitlistReason = await fetchSequenceEligibility(
            id,
            shouldAllow27to30BmiNoComorbidity
          );
          // a lack of waitlistReason means the user is eligible
          sequenceEligible = !waitlistReason;
          const wwEligible = isEligibleWw(answers, profile, questions);

          if (newQuiz.eli) {
            result = getResultsPageEli(
              waitlistReason,
              sequenceEligible,
              wwEligible
            );
          } else if (newQuiz.isSequence) {
            result = getResultsJoinSequence(
              waitlistReason,
              sequenceEligible,
              wwEligible
            );
          }
        }

        setEligibleSequence(sequenceEligible);
        setResultsScreen(result);

        const response = await assessmentService.post(
          "/api/assessments/new-quiz/save/v2",
          {
            answers: sanitizeAnswers(removeSensitiveData(answers), questions),
            slugs: newQuiz.dbSlugs,
            userPredictedToReachGoal,
            userRecommendedPlanLength,
            resultsScreen: result,
            eligibleSequence: sequenceEligible,
            result: product,
            finished: true
          }
        );

        const { status, data } = response;
        const aid = data["id"];

        if (status !== 200) {
          throw new Error();
        }

        setSuggestedProduct(product);
        setAssessmentId(aid);

        // Update user profile seqAssessmentId
        const { uuid } = getSessionData();
        const loggedInQuiz =
          newQuiz.isUni4 || newQuiz.isUni3 || newQuiz.isSequence;
        if (uuid && aid && loggedInQuiz) {
          await updateAssessmentId({
            seqAssessmentId: aid,
            market
          });
        } else if (uuid && loggedInQuiz) {
          logger("Error: no aid found when submitting assessment", {
            level: "error"
          });
        }

        // Create an ineligible user account for sequence quiz only.
        if (!sequenceEligible && newQuiz.isSequence && aid) {
          createIneligibleUser(aid);
        }

        setMode(MODE_SUCCESS);
      } catch (error) {
        logger("Error submitting assessment", {
          level: "error",
          customAttributes: { error }
        });
        setMode(MODE_FAILURE);
      }
    },
    []
  );

  return {
    assessmentId,
    suggestedProduct,
    submit,
    isSubmitting: mode === MODE_SUBMITTING,
    isSuccess: mode === MODE_SUCCESS,
    isFailure: mode === MODE_FAILURE,
    isPristine: mode === MODE_PRISTINE,
    eligibleSequence,
    resultsScreen
  };
}
